import { useEffect, useState } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';

import { PageContent } from '@components/page-containers/page-container';
import { MiddleColumnPage } from '@components/page-containers/page-containers';
import { Select } from '@components/select';
import { Stack } from '@components/stack';
import { LabeledSwitch } from '@components/switch';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';

import { useCompanyDetails, useCompanyTimezones } from 'api/company/hooks';
import { useCreateJourney, useUpdateJourneySettings } from 'api/journeys/hooks';
import { routePaths } from 'ui/authenticated/navigation';

type JourneySettingsProps = {
  journeyTimezone: string;
  notifyManualActionAssignees: boolean;
};

export const JourneySettings = ({
  journeyTimezone,
  notifyManualActionAssignees,
}: JourneySettingsProps) => {
  return (
    <PageContent>
      <MiddleColumnPage>
        <Stack space={4}>
          <JourneyTimezone journeyTimezone={journeyTimezone} />
          <JourneyNotifications notifyManualActionAssignees={notifyManualActionAssignees} />
        </Stack>
      </MiddleColumnPage>
    </PageContent>
  );
};

const JourneyTimezone = ({ journeyTimezone }: { journeyTimezone: string }) => {
  const { t } = useScopedTranslate(locale);
  const { journeyId, workspaceId = '' } = useParams();

  const companyQuery = useCompanyDetails();
  const timezones = useCompanyTimezones();
  const editJourneySettings = useUpdateJourneySettings();
  const createJourneyMutation = useCreateJourney();
  const navigate = useNavigate();

  const company = companyQuery.data;
  const timezone = journeyTimezone ?? company?.timezone?.split(' ')[1];

  const handleChange = async (data: string) => {
    let id = journeyId ?? '';
    let newJourney;

    if (!journeyId) {
      const { shortId } = await createJourneyMutation.mutateAsync({
        title: 'Untitled journey',
        workspace: workspaceId,
      });
      id = shortId;
      newJourney = true;
    }
    await editJourneySettings.mutateAsync({ id, timezone: data });

    if (newJourney) {
      navigate(
        generatePath(routePaths.editJourney, {
          workspaceId: workspaceId ?? '',
          journeyId: id,
          '*': 'settings',
        }),
        {
          replace: true,
        }
      );
    }
  };

  return (
    <>
      <Text font="bold18" text={t('timezoneTitle')} />
      <Stack>
        <Text font="bold14" text={t('timezoneCompany')} />
        <Text font="regular14" text={company?.timezone} />
      </Stack>
      <Stack space={1} style={{ maxWidth: pxToRem(419) }}>
        <Select
          id={journeyId}
          label={t('timezoneJourney')}
          options={timezones.data ?? []}
          defaultValue={timezone}
          isClearable={false}
          onChange={(data) => handleChange(data)}
        />
        <Text font="regular14" color="textSecondary" text={t('timezoneDescription')} />
      </Stack>
    </>
  );
};

const JourneyNotifications = ({
  notifyManualActionAssignees,
}: {
  notifyManualActionAssignees: boolean;
}) => {
  const { t } = useScopedTranslate(locale);
  const { journeyId } = useParams();
  const editJourneySettings = useUpdateJourneySettings();
  const [optimisticValue, setOptimisticValue] = useState(notifyManualActionAssignees);

  useEffect(() => {
    setOptimisticValue(notifyManualActionAssignees);
  }, [notifyManualActionAssignees]);

  const handleChange = (value: boolean) => {
    if (!journeyId || value === optimisticValue) {
      return;
    }
    setOptimisticValue(value);
    editJourneySettings.mutateAsync({
      id: journeyId,
      notifyManualActionAssignees: value,
    });
  };

  return (
    <Stack space={2}>
      <Text font="bold18" text={t('notificationsTitle')} />
      <LabeledSwitch
        label={t('notifyAssigneesLabel')}
        helpText={t('notifyAssigneesDescription')}
        helpTextPosition="bottom"
        value={optimisticValue}
        onChange={handleChange}
      />
    </Stack>
  );
};

const locale = {
  en: {
    active: 'Active',
    timezoneTitle: 'Timezone',
    timezoneDescription:
      'This timezone controls all time-based events in the journey, including actions and scheduled polling.',
    timezoneCompany: 'Company timezone',
    timezoneJourney: 'Journey timezone',
    notificationsTitle: 'Notifications',
    notifyAssigneesLabel: 'Email assigned users when an action is ready for them',
    notifyAssigneesDescription:
      'Sends an email to every assignee the moment a manual action becomes ready in this journey, with a link to the traveller journey',
  },
};
